import React, { useEffect, useRef, useMemo, useCallback } from "react";
import * as PIXI from "pixi.js";
import { Stage as PixiStage, Graphics } from "@pixi/react";
import { ReactReduxContext } from "react-redux";
import { DimensionsContext } from "./Contexts/DimensionsContext";
import { AppActivityContext } from "./Contexts/AppActivityContext";
import { ReelControlContext } from "./Contexts/ReelControlContext";
import { FreeSpinControlContext } from "./Contexts/FreeSpinControlContext";
import { FreeSpinBasicControlContext } from "./Contexts/FreeSpinBasicControlContext";
import { HoldAndWinControlContext } from "./Contexts/HoldAndWinControlContext";
// NOT BUILT (Cleopatra's Fire) — uncomment to bring back: import { RoyalCollectControlContext } from "./Contexts/RoyalCollectControlContext";
// NOT BUILT (Cleopatra's Fire) — uncomment to bring back: import { PotCollectionControlContext } from "./Contexts/PotCollectionControlContext";
import SlotRegistryContext from "./Contexts/SlotRegistryContext";

import { useDimensions } from "./Contexts/DimensionsContext";
import ContextBridge from "./Contexts/ContextBridge";
// NOT BUILT (Cleopatra's Fire) — uncomment to bring back: import BackgroundAnimation from "./Components/Animations/BackgroundAnimation/BackgroundAnimation";
// NOT BUILT (Cleopatra's Fire) — uncomment to bring back: import LegacyGameView from "./Components/Animations/GameView/GameView";
import GameViewSimple from "./Components/Animations/GameViewSimple/GameViewSimple";
// NOT USED (Cleopatra's Fire) — replaced by FreeSpinsGameView:
// import BonusGameView from "./Components/Animations/BonusGameView/BonusGameView";
import FreeSpinsGameView from "./Components/Animations/FreeSpinsGameView/FreeSpinsGameView";
import HoldAndWinGameView from "./Components/Animations/HoldAndWinGameView/HoldAndWinGameView";


// import Menu from "./Components/UserInterface/primary/Menu/Menu";
// import IntroPopup from "./Components/UserInterface/shared/Popups/IntroPopup/IntroPopup/IntroPopup";
import IntroPopup from "./Components/UserInterface/shared/Popups/IntroPopup/IntroPopup/IntroPopupComposed";
import IntroTransitionOverlay from "./Components/Animations/IntroTransitionOverlay/IntroTransitionOverlay";
import { isStandaloneReplayMode } from "./utils/launcherOptions";
import { startPerfProbe } from "./utils/perfProbe";
import ErrorRecoveryOverlay from "./Components/Animations/ErrorRecoveryOverlay/ErrorRecoveryOverlay";
import Transitions from "./Components/Animations/Transitions/Transitions";
// NOT BUILT (Cleopatra's Fire) — uncomment to bring back: import GameContainer from "./Games/Main/GameContainer/GameContainer";
// NOT BUILT (Cleopatra's Fire) — uncomment to bring back: import WinAnimationPrimary from "./Components/Animations/WinAnimation/WinAnimation";
import WinAnimationSecondary from "./Components/Animations/WinAnimation/WinAnimationSecondary";
import IosFullscreenOverlay from "./Components/Animations/IosFullscreenOverlay/IosFullscreenOverlay";
import { FEATURES_CONFIG } from "./featuresConfig";
import Dialogs from "./Components/Animations/Dialogs/Dialogs";
import { ReelRefsContext } from "./Contexts/ReelRefsContext";
import HookImport from "./Hooks/HookImport";
// NOT BUILT (Cleopatra's Fire) — uncomment to bring back: import PrimaryLoader from "./Components/UserInterface/primary/Other/Loader";
import SecondaryLoader from "./Components/UserInterface/secondary/Popups/Loader";
import SecondaryLoadingErrorPopup from "./Components/UserInterface/secondary/Popups/LoadingErrorPopup";
import { useSelector } from "react-redux";
import { Settings } from "./settings";

// Dynamic UI Theme imports
// NOT BUILT (Cleopatra's Fire) — uncomment to bring back: import PrimaryUserInterface from "./Components/UserInterface/primary/UserInterface";
import SecondaryUserInterface from "./Components/UserInterface/secondary/UserInterface";

// Secondary theme only — the primary imports above are not built.
// const UserInterface = Settings.APP.UI_THEME === "primary"
//   ? PrimaryUserInterface
//   : SecondaryUserInterface;
const UserInterface = SecondaryUserInterface;

// const Loader = Settings.APP.UI_THEME === "primary"
//   ? PrimaryLoader
//   : SecondaryLoader;
const Loader = SecondaryLoader;

// ── Which scene renders ──────────────────────────────────────────────────────
// GameViewSimple is the Mad Labs scene: one spine, one looping animation, one
// skin per orientation, and the two reel injection slots. Nothing else.
//
// The legacy GameView is Zeus's — it also carries pots, jackpots, a respin
// counter, the cloud transition, anticipation and rain, none of which this game
// has. It is kept only as a reference for the injection pattern and should be
// deleted once the simple scene is proven. Flip this to false to fall back.
// LegacyGameView is not built (import commented above), so the switch is too.
// const USE_SIMPLE_GAME_VIEW = true;
// const GameView = USE_SIMPLE_GAME_VIEW ? GameViewSimple : LegacyGameView;
const GameView = GameViewSimple;

// Primary keeps its error UI inside its own loader controller — only the
// secondary theme has it split out as a component.
const LoadingErrorPopup = Settings.APP.UI_THEME === "primary"
  ? null
  : SecondaryLoadingErrorPopup;

// const WinAnimation = Settings.APP.UI_THEME === "primary"
//   ? WinAnimationPrimary
//   : WinAnimationSecondary;
const WinAnimation = WinAnimationSecondary;

const StageWithDimensions = ({ children }) => {
  const { dimensions } = useDimensions();
  const stageRef = useRef(null);

  // Memoize contexts array (static array, doesn't change)
  const contexts = useMemo(
    () => [
      { Context: ReelRefsContext },
      { Context: SlotRegistryContext },
      { Context: ReactReduxContext },
      { Context: AppActivityContext },
      { Context: DimensionsContext },
      { Context: ReelControlContext },
      { Context: FreeSpinControlContext },
      // The Mad Labs round. Its own context, so it needs its own bridge entry —
      // FreeSpinControlContext above is Zeus's and shares nothing with it.
      { Context: FreeSpinBasicControlContext },
      { Context: HoldAndWinControlContext },
      // NOT BUILT (Cleopatra's Fire):
      // { Context: RoyalCollectControlContext },
      // { Context: PotCollectionControlContext },
    ],
    [],
  );

  // Memoize PixiStage options
  const stageOptions = useMemo(
    () => ({
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
      backgroundColor: 0x000000,
      antialias: false,
      powerPreference: "high-performance",
      preserveDrawingBuffer: false,
      clearBeforeRender: true,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (stageRef.current) {
      const app = stageRef.current.app;
      globalThis.__PIXI_APP__ = app;
      globalThis.__PIXI_STAGE__ = app.stage;
      globalThis.__PIXI_RENDERER__ = app.renderer;
      // Inert unless DEV_TOOLS.PERF_PROBE is on. Started here because this is
      // the line that makes the renderer reachable.
      startPerfProbe();
      globalThis.__PIXI_STAGE__.name = "stage";
      globalThis.__PIXI_STAGE__.sortableChildren = true;

      // ── Texture GC OFF ──
      //
      // PIXI stamps every texture with the frame it was last DRAWN and, in AUTO
      // mode, deletes anything untouched for `maxIdle` — 3600 frames, one
      // minute. The texture is then re-uploaded on the frame it is next needed,
      // inside that frame, which is a hitch exactly when something appears.
      //
      // That single rule is why so much of this game is hidden at `alpha 0.001`
      // instead of being hidden properly: 0.001 still RENDERS, which keeps the
      // stamp fresh. It buys nothing else — a texture stays on the GPU whether
      // or not it is drawn — so paying a draw call every frame, for ever, to
      // outrun a garbage collector is the wrong trade.
      //
      // MANUAL turns the sweep off. Nothing is ever unloaded, so nothing can go
      // cold, and a hidden thing can be hidden at `alpha 0` — which PIXI skips
      // outright. The cost is that GPU texture memory is never reclaimed on its
      // own, which for a game with a fixed asset set is what we want anyway;
      // `renderer.textureGC.run()` is still there if it is ever needed.
      if (app.renderer.textureGC) {
        app.renderer.textureGC.mode = PIXI.GC_MODES.MANUAL;
      }
    }
  }, []);

  // Keep renderer resolution in sync with devicePixelRatio
  // Covers: F12 devtools, window resize, browser zoom, monitor switching
  useEffect(() => {
    const updateResolution = () => {
      if (!stageRef.current) return;
      const renderer = stageRef.current.app.renderer;
      const newResolution = Math.min(window.devicePixelRatio || 1, 2);
      if (renderer.resolution !== newResolution) {
        renderer.resolution = newResolution;
        renderer.resize(dimensions.width, dimensions.height);
      }
    };

    // Update on dimension change
    updateResolution();

    // Also detect DPR changes independently (monitor switch, browser zoom)
    const mqString = `(resolution: ${window.devicePixelRatio}dppx)`;
    const mql = window.matchMedia(mqString);
    const handleDprChange = () => updateResolution();
    mql.addEventListener("change", handleDprChange);

    return () => mql.removeEventListener("change", handleDprChange);
  }, [dimensions.width, dimensions.height]);

  return (
    <ContextBridge
      contexts={contexts}
      render={(bridgeChildren) => (
        <PixiStage
          ref={stageRef}
          width={dimensions.width}
          height={dimensions.height}
          options={stageOptions}
          name="stage"
        >
          {bridgeChildren}
        </PixiStage>
      )}
    >
      {children}
    </ContextBridge>
  );
};

// ── Debug Crosshair Overlay ──────────────────────────────────────────────────
// Draws 1px red lines at the vertical and horizontal center of the screen.
// Controlled by FEATURES_CONFIG.DEV_TOOLS.DEBUG_CROSSHAIR.
const DebugCrosshair = () => {
  const { dimensions } = useDimensions();
  const draw = useCallback((g) => {
    g.clear();
    const cx = dimensions.width / 2;
    const cy = dimensions.height / 2;
    // Horizontal line
    g.lineStyle(1, 0xff0000, 0.8);
    g.moveTo(0, cy);
    g.lineTo(dimensions.width, cy);
    // Vertical line
    g.moveTo(cx, 0);
    g.lineTo(cx, dimensions.height);
  }, [dimensions.width, dimensions.height]);

  return <Graphics draw={draw} zIndex={99999} />;
};

const Stage = () => {
  const { loaderComplete, introMounted, status } = useSelector(
    (state) => state.loadingProgress,
  );
  // Init failed. Nothing below may mount — see loadingProgressSlice, which
  // also refuses to set the two flags once this is true. Belt and braces: the
  // flags may already have been set before the rejection arrived.
  const loadingFailed = status === "error";

  return (
    <>
      <StageWithDimensions>
        <HookImport />

        <Loader />

        {/* Intro popup mounts as soon as the loader's spine has done one
            revolution and the intro's own force-loaded assets have landed —
            deliberately BEFORE the game is ready. The remaining textures,
            spines and sounds download behind it while the player reads the
            feature slider; the popi spine sits where the tap-to-start text
            goes until everything is in. It is fully opaque, so the game tree
            below mounts behind it unseen, exactly as it used to mount behind
            the loader. */}
        {introMounted && !loadingFailed && !isStandaloneReplayMode() && (
          <IntroPopup />
        )}

        {/* Owns the screen outright when init fails. No dismiss, no path
            onward — the only action is Reload. */}
        {loadingFailed && LoadingErrorPopup && <LoadingErrorPopup />}

        {/* Game mounts once everything has loaded — hidden behind the intro popup */}
        {loaderComplete && !loadingFailed && (
          <>
            {/* GameView replaces both BackgroundAnimation and GameContainer.
                The bonus room is a SECOND spine in this game rather than a
                skin of the first, so it mounts alongside on the SAME layer.
                Only one of the two is ever visible; the scene swap hides one
                and shows the other. */}
            {FEATURES_CONFIG.GAME_VIEW.ENABLED
              ? <>
                  <GameView />
                  {/* Gated like every other feature scene: with the flag off,
                      the bonus room, its spine and its second board leave the
                      tree entirely. GameView mounts PotCollectionReelSet the
                      same way. */}
                  {/* NOT USED: {FEATURES_CONFIG.FREE_SPINS.ENABLED && <BonusGameView />} */}
                  {FEATURES_CONFIG.FREE_SPINS.ENABLED && <FreeSpinsGameView />}
                  {/* Cleopatra Hold & Win room — scene only for now. Drawn
                      last, so the dev preview shows it over the other two. */}
                  <HoldAndWinGameView />
                </>
              : null /* NOT BUILT: <BackgroundAnimation /> — GAME_VIEW is on */
            }
            {/* Standalone replay link (docs/replay-history-spec.md §0 Stage
                9/10): no intro ceremony at all — loader fades straight into
                the game with the replay Enter modal on top. Both the intro
                popup and its black cover-and-reveal transition are skipped
                outright rather than left mounted-but-inactive, so neither
                can put a full-screen layer over a session the player can
                only ever watch one round in. */}
            {!isStandaloneReplayMode() && <IntroTransitionOverlay />}
            <ErrorRecoveryOverlay />
            <UserInterface>
              {/* GameContainer only renders when GameView is disabled */}
              {/* NOT BUILT: {!FEATURES_CONFIG.GAME_VIEW.ENABLED && <GameContainer />} */}
            </UserInterface>
            <Transitions />
            <Dialogs />
            <WinAnimation />
            {FEATURES_CONFIG.IOS_FULLSCREEN_OVERLAY.ENABLED && <IosFullscreenOverlay />}
            {FEATURES_CONFIG.DEV_TOOLS.DEBUG_CROSSHAIR && <DebugCrosshair />}

          </>
        )}
      </StageWithDimensions>
    </>
  );
};

export default Stage;
